import React, { useMemo, useState } from 'react';

/**
 * Nakladka postepu podczas zbiorczego importu paragonow XML.
 * Paczka idzie porcjami, wiec licznik skacze co kilka plikow.
 */
const MultiParagonProgress = ({ progress }) => {
  if (!progress) return null;

  const procent = progress.total ? Math.round((progress.current / progress.total) * 100) : 0;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'var(--card-bg)', borderRadius: '8px', padding: '2rem', textAlign: 'center'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '1rem' }}>{'🧾'}</div>
        <h5 style={{ margin: '0 0 0.5rem 0' }}>Zapisywanie paragon{'ó'}w...</h5>
        <div style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
          Zapisano {progress.current} z {progress.total}
          {progress.currentFile ? ` — teraz: ${progress.currentFile}` : ''}
        </div>
        <div style={{ height: '8px', backgroundColor: 'var(--border)', borderRadius: '4px', width: '300px', overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${procent}%`,
            backgroundColor: 'var(--success)', transition: 'width 0.3s ease'
          }} />
        </div>
      </div>
    </div>
  );
};

const STATUSY = {
  zapisany: { ikona: '✅', etykieta: 'Zapisany', kolor: 'var(--success)', tlo: 'var(--success-light)' },
  duplikat: { ikona: '⏭', etykieta: 'Pominięty (jest już w systemie)', kolor: 'var(--warning)', tlo: 'var(--warning-light)' },
  blad: { ikona: '❌', etykieta: 'Błąd', kolor: 'var(--danger)', tlo: 'var(--danger-light)' },
};

const formatKwota = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `${n.toFixed(2)} zł` : '-';
};

const formatIlosc = (v) => {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return '-';
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const th = (tekst, extra = {}) => (
  <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', ...extra }}>{tekst}</th>
);
const td = { padding: '0.35rem 0.5rem', borderBottom: '1px solid var(--border)' };

/**
 * Raport po zbiorczym imporcie paragonow.
 *
 * Dwie listy, kazda z WLASNYM suwakiem i bez przycinania:
 *   1. paragony - jeden wiersz na plik (zapisany / duplikat / blad),
 *   2. wszystkie produkty, ktore weszly z zapisanych paragonow.
 * Nie ma tu "Zapisz" - zapis juz sie odbyl na serwerze.
 */
const MultiParagonImportModal = ({ wynik, onClose, onOtworzFakture }) => {
  const [szukaj, setSzukaj] = useState('');

  const p = wynik?.podsumowanie || {};
  const wiersze = useMemo(() => (Array.isArray(wynik?.wyniki) ? wynik.wyniki : []), [wynik]);

  // Produkty z paragonow ZAPISANYCH (duplikaty i bledy niczego nie wgraly).
  const produkty = useMemo(() => {
    const lista = [];
    wiersze.forEach((w) => {
      if (w.status !== 'zapisany' || !Array.isArray(w.pozycje_lista)) return;
      w.pozycje_lista.forEach((poz, i) => lista.push({ ...poz, _plik: w.plik, _numer: w.numer, _lp: i + 1 }));
    });
    return lista;
  }, [wiersze]);

  const produktyFiltr = useMemo(() => {
    const q = szukaj.trim().toLowerCase();
    if (!q) return produkty;
    return produkty.filter(x =>
      (x.nazwa || '').toLowerCase().includes(q)
      || (x.nazwa_w_systemie || '').toLowerCase().includes(q)
      || (x.kod || '').toLowerCase().includes(q)
      || (x.ean || '').toLowerCase().includes(q)
      || (x._numer || '').toLowerCase().includes(q)
      || (x._plik || '').toLowerCase().includes(q)
    );
  }, [produkty, szukaj]);

  if (!wynik) return null;

  const niezmapowane = produkty.filter(x => !x.zmapowany).length;

  const kafel = (wartosc, etykieta, kolor, tlo) => (
    <div style={{ textAlign: 'center', padding: '0.4rem 0.9rem', backgroundColor: tlo, borderRadius: '4px', minWidth: '90px' }}>
      <div style={{ fontSize: '18px', fontWeight: '700', color: kolor }}>{wartosc}</div>
      <div style={{ fontSize: '10px', color: kolor }}>{etykieta}</div>
    </div>
  );

  const naglowekSekcji = (tekst, prawa) => (
    <div style={{
      padding: '0.4rem 0.75rem', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)',
      backgroundColor: 'var(--bg)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexShrink: 0
    }}>
      <span>{tekst}</span>
      {prawa}
    </div>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'var(--card-bg)', borderRadius: '8px', width: '96%', maxWidth: '1400px',
        height: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }}>
        {/* Naglowek */}
        <div style={{
          padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', flexShrink: 0,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          backgroundColor: 'var(--success)'
        }}>
          <div style={{ color: '#fff' }}>
            <h5 style={{ margin: 0, fontSize: '16px' }}>{'🧾'} Import wielu paragon{'ó'}w XML</h5>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>
              {p.plikow || 0} plik{'ó'}w | zapisano {p.zapisane || 0} | {formatKwota(p.suma_brutto_zapisanych)} brutto | {produkty.length} produkt{'ó'}w
            </div>
          </div>
          <button onClick={onClose} aria-label="Zamknij okno"
            style={{ background: 'var(--bg-secondary)', color: 'var(--text)',
              fontSize: '20px', cursor: 'pointer', padding: '4px 10px' }}>{'×'}</button>
        </div>

        {/* Podsumowanie */}
        <div style={{ padding: '0.5rem 1rem', flexShrink: 0, display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {kafel(p.zapisane || 0, '✅ Zapisane', STATUSY.zapisany.kolor, STATUSY.zapisany.tlo)}
          {kafel(p.duplikaty || 0, '⏭ Pominięte (już w systemie)', STATUSY.duplikat.kolor, STATUSY.duplikat.tlo)}
          {kafel(p.bledy || 0, '❌ Błędy', STATUSY.blad.kolor, STATUSY.blad.tlo)}
          {kafel(produkty.length, '📦 Produkty', 'var(--info)', 'var(--info-light)')}
          {(p.duplikaty || 0) > 0 && (
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', maxWidth: '420px' }}>
              Pominięty = paragon o tym numerze jest już w systemie (w którymkolwiek sklepie — komunikat mówi, w którym). Nic nie zostało nadpisane ani dodane drugi raz.
            </div>
          )}
        </div>

        {/* 1. Lista paragonow - wlasny suwak */}
        {naglowekSekcji(`Paragony (${wiersze.length})`)}
        <div data-testid="lista-paragonow" style={{ flex: '0 0 auto', maxHeight: '30vh', minHeight: '90px', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg)', zIndex: 10 }}>
              <tr>
                {th('Status', { width: '140px' })}
                {th('Plik')}
                {th('Numer', { width: '150px' })}
                {th('Dostawca')}
                {th('Poz.', { textAlign: 'right', width: '55px' })}
                {th('Brutto', { textAlign: 'right', width: '95px' })}
                {th('Komunikat')}
              </tr>
            </thead>
            <tbody>
              {wiersze.map((w, idx) => {
                const s = STATUSY[w.status] || STATUSY.blad;
                const klik = w.status === 'zapisany' && w.invoice_id && onOtworzFakture;
                return (
                  <tr key={idx} style={{ backgroundColor: w.status === 'zapisany' ? (idx % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)') : s.tlo }}>
                    <td style={{ ...td, color: s.kolor, fontWeight: '600', whiteSpace: 'nowrap' }}>{s.ikona} {s.etykieta}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: '10px', wordBreak: 'break-all' }}>{w.plik}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {klik ? (
                        <button onClick={() => onOtworzFakture(w.invoice_id)} title="Otwórz zapisany dokument"
                          style={{ padding: '2px 6px', fontSize: '11px', border: '1px solid var(--primary)', color: 'var(--primary)', cursor: 'pointer', backgroundColor: 'var(--card-bg)' }}>
                          {w.numer || `#${w.invoice_id}`}
                        </button>
                      ) : (w.numer || '-')}
                    </td>
                    <td style={{ ...td, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.dostawca || '-'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{w.pozycji ?? '-'}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: '600' }}>{formatKwota(w.suma_brutto)}</td>
                    <td style={{ ...td, color: w.status === 'zapisany' ? 'var(--text-muted)' : s.kolor }}>{w.komunikat || ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {wiersze.length === 0 && (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>Brak wynik{'ó'}w</div>
          )}
        </div>

        {/* 2. Wszystkie produkty z zapisanych paragonow - wlasny suwak, bez przycinania */}
        {naglowekSekcji(
          `Produkty z zapisanych paragonów (${produktyFiltr.length}${szukaj ? ` z ${produkty.length}` : ''})`
          + (niezmapowane ? ` — bez karty w systemie: ${niezmapowane}` : ''),
          <input
            type="text"
            placeholder={'🔍 Szukaj: nazwa, kod, EAN, numer paragonu'}
            value={szukaj}
            onChange={e => setSzukaj(e.target.value)}
            style={{
              padding: '4px 8px', fontSize: '11px', borderRadius: '4px',
              border: '1px solid var(--border)', minWidth: '260px',
              backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontWeight: '400'
            }}
          />
        )}
        <div data-testid="lista-produktow" style={{ flex: 1, minHeight: '160px', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg)', zIndex: 10 }}>
              <tr>
                {th('Paragon', { width: '140px' })}
                {th('Lp', { textAlign: 'right', width: '40px' })}
                {th('Nazwa z paragonu')}
                {th('Nazwa w systemie')}
                {th('Kod', { width: '110px' })}
                {th('EAN', { width: '130px' })}
                {th('Ilość', { textAlign: 'right', width: '60px' })}
                {th('Cena brutto', { textAlign: 'right', width: '95px' })}
                {th('Wartość', { textAlign: 'right', width: '95px' })}
                {th('VAT', { textAlign: 'right', width: '55px' })}
              </tr>
            </thead>
            <tbody>
              {produktyFiltr.map((x, idx) => (
                <tr key={`${x._plik}-${x._lp}`} style={{ backgroundColor: x.zmapowany ? (idx % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)') : 'var(--warning-light)' }}>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontSize: '10px' }} title={x._plik}>{x._numer || x._plik}</td>
                  <td style={{ ...td, textAlign: 'right', color: 'var(--text-muted)' }}>{x._lp}</td>
                  <td style={{ ...td, maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={x.nazwa}>{x.nazwa || '-'}</td>
                  <td style={{ ...td, maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: x.zmapowany ? 'var(--success)' : 'var(--warning)' }} title={x.nazwa_w_systemie || ''}>
                    {x.zmapowany ? (x.nazwa_w_systemie || '✓') : '(brak karty — do zmapowania)'}
                  </td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: '10px' }}>{x.kod || '-'}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: '10px' }}>{x.ean || '-'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{formatIlosc(x.ilosc)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{formatKwota(x.cena_brutto)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: '600' }}>{formatKwota(x.wartosc_brutto)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{x.stawka_vat != null ? `${formatIlosc(x.stawka_vat)}%` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {produktyFiltr.length === 0 && (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              {produkty.length === 0 ? 'Żaden paragon nie został zapisany — brak produktów' : 'Brak produktów pasujących do wyszukiwania'}
            </div>
          )}
        </div>

        {/* Stopka */}
        <div style={{ padding: '0.6rem 1rem', borderTop: '1px solid var(--border)', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--bg)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            Obie listy przewijają się osobno — wszystkie wiersze są załadowane.
          </div>
          <button onClick={onClose}
            style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: 'var(--primary)',
              color: '#fff', cursor: 'pointer', fontWeight: '600' }}>
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
};

export { MultiParagonProgress };
export default MultiParagonImportModal;
